import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation, useParams } from 'react-router-dom';

// project imports
import { useAppDispatch, useAppSelector } from 'hooks/useAppStore';
import {
  selectSelectedEmsMemberAccount,
  selectTenant
} from 'features/account/accountSelectors';
import {
  GRID_SPACING,
  NavItemsForDisplayBalanceGroupEntity,
  NavItemsForDisplayDeliveryContractEntity,
  NavItemsForDisplayDeliveryPointEntity,
  NavItemsForDisplayDeliveryPointGdsEntity,
  NavItemsForDisplayDeviceEntity,
  NavItemsForDisplaySubjectEntity,
  NavItemsForDisplayUserEntity,
  Routes
} from 'utils/constants';
import { selectDeviceDetailList } from 'features/eai/selectors/deviceSelectors';
import { selectDeliveryPointDetail } from 'features/eai/selectors/deliveryPointSelectors';
import { selectUserDetail } from 'features/eai/selectors/userSelectors';
import { selectEmsPartyDetail } from 'features/eai/selectors/emsPartySelectors';
import { selectBalanceGroupDetail } from 'features/eai/selectors/balanceGroupSelectors';
import { selectDeliveryContractDetail } from 'features/eai/selectors/deliveryContractSelectors';
import { IconTallymark1 } from '@tabler/icons';
import { useTheme } from '@mui/material/styles';
import { Box, Card, Divider, Grid, Typography } from '@mui/material';
import MuiBreadcrumbs from '@mui/material/Breadcrumbs';
import AccountTreeTwoToneIcon from '@mui/icons-material/AccountTreeTwoTone';
import HomeIcon from '@mui/icons-material/Home';
import HomeTwoToneIcon from '@mui/icons-material/HomeTwoTone';
import { NavItemType, NavItemTypeObject, OverrideIcon } from 'models';
import { setActiveItem } from 'features/app/appSlice';
import { replaceDynamicPathParams } from 'routes';

interface BreadCrumbSxProps extends React.CSSProperties {
  mb?: string;
  bgcolor?: string;
}

interface BreadCrumbsProps {
  card?: boolean;
  divider?: boolean;
  icon?: boolean;
  icons?: boolean;
  maxItems?: number;
  navigation?: NavItemTypeObject;
  rightAlign?: boolean;
  separator: OverrideIcon;
  title?: boolean;
  titleBottom?: boolean;
  sx?: BreadCrumbSxProps;
}

const createBreadcrumbs = (
  navigationItem: NavItemType | undefined,
  pathname: string,
  tenant: string | null,
  id: string | undefined,
  userId: string | undefined,
  deviceGroupId: string | undefined,
  deliveryPointId: string | undefined,
  deviceId: string | undefined,
  gdsId: string | undefined,
  balanceGroupId: string | undefined
): NavItemType[] | null => {
  if (!navigationItem) return null;

  if (
    replaceDynamicPathParams(navigationItem.url || '', [
      { paramName: 'tenant', value: tenant },
      { paramName: 'id', value: id || null },
      { paramName: 'userId', value: userId || null },
      { paramName: 'deviceGroupId', value: deviceGroupId || null },
      { paramName: 'deliveryPointId', value: deliveryPointId || null },
      { paramName: 'deviceId', value: deviceId || null },
      { paramName: 'gdsId', value: gdsId || null },
      { paramName: 'balanceGroupId', value: balanceGroupId || null }
    ]) === pathname
  ) {
    return [navigationItem];
  }

  if (navigationItem.children) {
    for (const navigationItemChild of navigationItem.children) {
      const path = createBreadcrumbs(
        navigationItemChild,
        pathname,
        tenant,
        id,
        userId,
        deviceGroupId,
        deliveryPointId,
        deviceId,
        gdsId,
        balanceGroupId
      );

      if (path) {
        return [navigationItem, ...path];
      }
    }
  }

  return null;
};

// constants
const linkSX = {
  display: 'flex',
  color: 'grey.900',
  textDecoration: 'none',
  alignContent: 'center',
  alignItems: 'center'
};

// ==============================|| BREADCRUMBS ||============================== //

const Breadcrumbs = ({
  card,
  divider,
  icon,
  icons,
  maxItems,
  navigation,
  rightAlign,
  separator,
  title,
  titleBottom,
  ...others
}: BreadCrumbsProps) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const {
    id,
    userId,
    deviceGroupId,
    deliveryPointId,
    deviceId,
    gdsId,
    balanceGroupId
  } = useParams();
  const dispatch = useAppDispatch();

  const tenant = useAppSelector(selectTenant);
  const selectedEmsMemberAccount = useAppSelector(
    selectSelectedEmsMemberAccount
  );
  const deviceDetail = useAppSelector(selectDeviceDetailList)[0];
  const deliveryPointDetail = useAppSelector(selectDeliveryPointDetail);
  const userDetail = useAppSelector(selectUserDetail);
  const subjectDetail = useAppSelector(selectEmsPartyDetail);
  const balanceGroupDetail = useAppSelector(selectBalanceGroupDetail);
  const deliveryContractDetail = useAppSelector(selectDeliveryContractDetail);

  const iconStyle = {
    marginRight: theme.spacing(0.75),
    marginTop: `-${theme.spacing(0.25)}`,
    width: '16px',
    height: '16px',
    color: theme.palette.secondary.main
  };

  // item separator
  const SeparatorIcon = separator;
  const separatorIcon = separator ? (
    <SeparatorIcon stroke={1.5} size='16px' />
  ) : (
    <IconTallymark1 stroke={1.5} size='16px' />
  );

  let breadcrumbContent: React.ReactElement = <></>;

  const [breadcrumbItems, setBreadcrumbItems] = useState<NavItemType[]>([]);

  useEffect(() => {
    const items = selectedEmsMemberAccount
      ? navigation?.items?.reduce((acc: NavItemType[], item: NavItemType) => {
          const breadcrumbs = createBreadcrumbs(
            item,
            pathname,
            tenant,
            id,
            userId,
            deviceGroupId,
            deliveryPointId,
            deviceId,
            gdsId,
            balanceGroupId
          );

          !!breadcrumbs?.length &&
            breadcrumbs?.forEach((navItem) => {
              !!navItem.title && navItem.type === 'item' && acc.push(navItem);
            });

          return acc;
        }, [])
      : [];

    items && setBreadcrumbItems(items);
  }, [
    selectedEmsMemberAccount,
    navigation?.items,
    pathname,
    tenant,
    id,
    userId,
    deviceGroupId,
    deliveryPointId,
    deviceId,
    gdsId,
    balanceGroupId
  ]);

  const addIdToNavItem = (item: NavItemType): string => {
    if (item.id) {
      if (NavItemsForDisplayDeviceEntity.includes(item.id)) {
        return createEntityDisplayName(
          deviceDetail?.deviceName,
          deviceId || id
        );
      }
      if (NavItemsForDisplayDeliveryPointEntity.includes(item.id)) {
        return createEntityDisplayName(
          deliveryPointDetail?.name,
          deliveryPointId || id
        );
      }
      if (
        NavItemsForDisplayUserEntity.includes(item.id) &&
        userDetail?.firstName &&
        userDetail?.lastName
      ) {
        return createEntityDisplayName(
          userDetail?.firstName + ' ' + userDetail?.lastName,
          userDetail?.id
        );
      }
      if (NavItemsForDisplaySubjectEntity.includes(item.id)) {
        return createEntityDisplayName(subjectDetail?.name);
      }
      if (NavItemsForDisplayBalanceGroupEntity.includes(item.id)) {
        return createEntityDisplayName(balanceGroupDetail?.name);
      }
      if (NavItemsForDisplayDeliveryContractEntity.includes(item.id)) {
        return createEntityDisplayName(deliveryContractDetail?.name, id);
      }
      if (NavItemsForDisplayDeliveryPointGdsEntity.includes(item.id)) {
        return createEntityDisplayName(gdsId);
      }
    }
    return '';
  };

  const createEntityDisplayName = (
    name?: string,
    entityId?: string
  ): string => {
    if (name && entityId) {
      return ` ${name} (${entityId})`;
    }

    if (entityId || name) {
      return ` ${entityId || name}`;
    }

    return '';
  };

  const getItemTitle = (item: NavItemType): string => {
    return t(item.title || '') + addIdToNavItem(item);
  };

  const getItemContent = (
    item: NavItemType,
    index: number
  ): React.ReactElement => {
    const ItemIcon = item.icon ? item.icon : AccountTreeTwoToneIcon;

    return breadcrumbItems.length - 1 !== index ? (
      <Typography
        key={index}
        component={Link}
        to={
          item.url
            ? replaceDynamicPathParams(item.url || '', [
                { paramName: 'tenant', value: tenant },
                { paramName: 'userId', value: userId },
                { paramName: 'id', value: id },
                { paramName: 'deliveryPointId', value: deliveryPointId },
                { paramName: 'deviceId', value: deviceId },
                { paramName: 'balanceGroupId', value: balanceGroupId }
              ])
            : '#'
        }
        variant='subtitle1'
        sx={linkSX}
      >
        {icons && <ItemIcon style={iconStyle} />}
        {getItemTitle(item)}
      </Typography>
    ) : (
      <Typography
        key={index}
        variant='subtitle1'
        sx={{
          display: 'flex',
          textDecoration: 'none',
          alignContent: 'center',
          alignItems: 'center',
          color: 'grey.500'
        }}
      >
        {icons && <ItemIcon style={iconStyle} />}
        {getItemTitle(item)}
      </Typography>
    );
  };

  // items
  if (breadcrumbItems.length) {
    // main
    if (breadcrumbItems[breadcrumbItems.length - 1].breadcrumbs !== false) {
      breadcrumbContent = (
        <Card
          sx={{
            marginBottom: card === false ? 0 : theme.spacing(GRID_SPACING),
            // border: card === false ? 'none' : '1px solid',
            // borderColor: theme.palette.mode === 'dark' ? theme.palette.background.default : theme.palette.primary[200] + 75,
            background:
              card === false ? 'transparent' : theme.palette.background.default
          }}
          {...others}
        >
          <Box sx={{ p: 2, pl: card === false ? 0 : 2 }}>
            <Grid
              container
              direction={rightAlign ? 'row' : 'column'}
              justifyContent={rightAlign ? 'space-between' : 'flex-start'}
              alignItems={rightAlign ? 'center' : 'flex-start'}
              spacing={1}
            >
              <Grid item>
                <MuiBreadcrumbs
                  sx={{
                    '& .MuiBreadcrumbs-separator': {
                      width: 16,
                      ml: 1.25,
                      mr: 1.25
                    }
                  }}
                  aria-label='breadcrumb'
                  maxItems={maxItems || 8}
                  separator={separatorIcon}
                >
                  <Typography
                    component={Link}
                    to={`${Routes.BASE_PATH}${tenant}`}
                    color='inherit'
                    variant='subtitle1'
                    sx={linkSX}
                    onClick={() => dispatch(setActiveItem([]))}
                  >
                    {icons && <HomeTwoToneIcon sx={iconStyle} />}
                    {icon && <HomeIcon sx={{ ...iconStyle, mr: 0 }} />}
                    {!icon && 'Dashboard'}
                  </Typography>
                  {breadcrumbItems.map((item, index) =>
                    getItemContent(item, index)
                  )}
                </MuiBreadcrumbs>
              </Grid>
              {
                /*todo*/ title && titleBottom && (
                  <Grid item>
                    <Typography variant='h3' sx={{ fontWeight: 500 }}>
                      {/*item.title*/}
                    </Typography>
                  </Grid>
                )
              }
            </Grid>
          </Box>
          {card === false && divider !== false && (
            <Divider
              sx={{ borderColor: theme.palette.primary.main, mb: GRID_SPACING }}
            />
          )}
        </Card>
      );
    }
  }
  return breadcrumbContent;
};

export default Breadcrumbs;
